import React, { useState, useEffect } from 'react';
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  Slide,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import FilterListIcon from '@mui/icons-material/FilterList';
import NavbarDashboard from '../Dashboard/NavbarDashboard';
import ResponsiveDrawer from '../Dashboard/Sidebar';

// Initialize Firebase
const firestore = getFirestore();

// Transition for dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const UserReferrals = ({ userPermissions }) => {
  const [users, setUsers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailSearchTerm, setDetailSearchTerm] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);
  const [editedPoints, setEditedPoints] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pointsFilter, setPointsFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch users and referrals data
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch users collection
      const usersSnapshot = await getDocs(collection(firestore, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        referralCount: 0, // Initialize referral count
        referredUsers: [] // Initialize referred users array
      }));

      // Fetch referrals collection
      const referralsSnapshot = await getDocs(collection(firestore, 'referrals'));
      const referralsData = referralsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setReferrals(referralsData);

      // Count referrals for each user and get referred user details
      const usersWithReferralCount = await Promise.all(usersData.map(async (user) => {
        const userReferrals = referralsData.filter(ref => ref.id === user.id);
        let referredToCount = 0;
        const referredUsers = [];

        for (const ref of userReferrals) {
          if (ref.referred_to && Array.isArray(ref.referred_to)) {
            referredToCount += ref.referred_to.length;
            
            // Get details for each referred user
            for (const referredUserId of ref.referred_to) {
              try {
                const userDoc = await getDoc(doc(firestore, 'users', referredUserId));
                if (userDoc.exists()) {
                  referredUsers.push({
                    id: referredUserId,
                    ...userDoc.data()
                  });
                } else {
                  referredUsers.push({
                    id: referredUserId,
                    name: 'Unknown User',
                    email: 'N/A',
                    phone: 'N/A'
                  });
                }
              } catch (err) {
                console.error(`Error fetching user ${referredUserId}:`, err);
                referredUsers.push({
                  id: referredUserId,
                  name: 'Error Loading User',
                  email: 'N/A',
                  phone: 'N/A'
                });
              }
            }
          }
        }
        
        return {
          ...user,
          referralCount: referredToCount,
          referredUsers: referredUsers
        };
      }));

const sortedUsers = usersWithReferralCount.sort((a, b) => {
  const countDiff = (b.referralCount || 0) - (a.referralCount || 0);
  if (countDiff !== 0) return countDiff;

  return (a.name || "").localeCompare(b.name || "");
});
      setUsers(sortedUsers);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Open detail dialog for a user
  const handleOpenDetails = (user) => {
    setSelectedUser(user);
    setDetailSearchTerm('');
    setDetailDialogOpen(true);
  };

  // Close detail dialog
  const handleCloseDetails = () => {
    setDetailDialogOpen(false);
    setSelectedUser(null);
  };

  // Start editing points for a user
  const handleEditPoints = (user) => {
    setEditingUserId(user.id);
    setEditedPoints(user.points || '');
  };

  // Save updated points for a user
  const handleSavePoints = async (userId) => {
    try {
      // Update in Firebase
      await updateDoc(doc(firestore, 'users', userId), {
        points: parseInt(editedPoints) || 0
      });

      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, points: parseInt(editedPoints) || 0 } : user
      ));

      setEditingUserId(null);
      setEditedPoints('');
    } catch (err) {
      console.error('Error updating points:', err);
      setError('Failed to update points. Please try again later.');
    }
  };

  // Cancel editing points
  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditedPoints('');
  };

  // Filter users based on search term and filters
  const filteredUsers = users.filter(user => {
    // Search filter
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm);
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.referralCount > 0) ||
      (statusFilter === 'inactive' && user.referralCount === 0);
    
    // Points filter
    const matchesPoints = pointsFilter === 'all' ||
      (pointsFilter === 'hasPoints' && user.points > 0) ||
      (pointsFilter === 'noPoints' && (!user.points || user.points === 0));
    
    return matchesSearch && matchesStatus && matchesPoints;
  });

  // Filter and sort referred users: name ASC
  const filteredReferredUsers = selectedUser ? selectedUser.referredUsers
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .filter(user =>
      user.name?.toLowerCase().includes(detailSearchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(detailSearchTerm.toLowerCase()) ||
      user.phone?.includes(detailSearchTerm) ||
      user.id?.includes(detailSearchTerm)
    ) : [];

  if (loading) {
    return (
      <div>
        <NavbarDashboard />
        <Box height={10} />
        <Box sx={{ display: 'flex' }}>
          <ResponsiveDrawer userPermissions={userPermissions} />
          <Box component="main" sx={{ flexGrow: 1, p: 9, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <NavbarDashboard />
      <Box height={10} />
      <Box sx={{ display: 'flex' }}>
        <ResponsiveDrawer userPermissions={userPermissions} />
        <Box component="main" sx={{ flexGrow: 1, p: 9 }}>
          <div className="container">
            <Paper elevation={3} sx={{ p: 4 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                  User Referral Management
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label={`Total Users: ${users.length}`} 
                    color="primary" 
                    variant="outlined"
                  />
                  <Button 
                    variant="outlined" 
                    startIcon={<FilterListIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    Filters
                  </Button>
                </Box>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Search Users by Name, Email or Phone"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      endAdornment: <SearchIcon />
                    }}
                  />
                </Grid>
                
                {showFilters && (
                  <>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={statusFilter}
                          label="Status"
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <MenuItem value="all">All Status</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="inactive">Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Points</InputLabel>
                        <Select
                          value={pointsFilter}
                          label="Points"
                          onChange={(e) => setPointsFilter(e.target.value)}
                        >
                          <MenuItem value="all">All Points</MenuItem>
                          <MenuItem value="hasPoints">Has Points</MenuItem>
                          <MenuItem value="noPoints">No Points</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>

              <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>User ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Phone</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Number of Referrals</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Points</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <TableRow key={user.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {user.id}
                          </TableCell>
                          <TableCell>{user.name || 'N/A'}</TableCell>
                          <TableCell>{user.email || 'N/A'}</TableCell>
                          <TableCell>{user.phone || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={user.referralCount} 
                              color={user.referralCount > 0 ? "success" : "default"}
                              variant={user.referralCount > 0 ? "filled" : "outlined"}
                              onClick={() => user.referralCount > 0 && handleOpenDetails(user)}
                              style={{ cursor: user.referralCount > 0 ? 'pointer' : 'default' }}
                            />
                          </TableCell>
                          <TableCell>
                            {editingUserId === user.id ? (
                              <Box display="flex" alignItems="center">
                                <TextField
                                  type="number"
                                  value={editedPoints}
                                  onChange={(e) => setEditedPoints(e.target.value)}
                                  size="small"
                                  sx={{ width: 80, mr: 1 }}
                                />
                                <IconButton 
                                  color="primary" 
                                  onClick={() => handleSavePoints(user.id)}
                                  size="small"
                                >
                                  <SaveIcon />
                                </IconButton>
                                <IconButton 
                                  color="secondary" 
                                  onClick={handleCancelEdit}
                                  size="small"
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box display="flex" alignItems="center">
                                <span>{user.points || 0}</span>
                                <IconButton 
                                  color="primary" 
                                  onClick={() => handleEditPoints(user)}
                                  size="small"
                                  sx={{ ml: 1 }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={user.referralCount > 0 ? "Active" : "Inactive"} 
                              color={user.referralCount > 0 ? "success" : "default"}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton 
                              color="primary" 
                              onClick={() => handleOpenDetails(user)}
                              disabled={user.referralCount === 0}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <Typography variant="body1" color="textSecondary">
                            No users found matching your search criteria.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Showing {filteredUsers.length} of {users.length} users
                </Typography>
                <Chip 
                  label={`Total Referrals: ${referrals.length}`} 
                  color="secondary" 
                  variant="outlined"
                />
              </Box>
            </Paper>
          </div>
        </Box>
      </Box>

      {/* Detail Dialog */}
      <Dialog
        fullScreen
        open={detailDialogOpen}
        onClose={handleCloseDetails}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseDetails}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Referral Details for {selectedUser?.name}
            </Typography>
          </Toolbar>
        </AppBar>

        <DialogContent>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">
                Referred Users: {selectedUser?.referralCount}
              </Typography>
              
              <TextField
                label="Search Referred Users"
                variant="outlined"
                value={detailSearchTerm}
                onChange={(e) => setDetailSearchTerm(e.target.value)}
                InputProps={{
                  endAdornment: <SearchIcon />
                }}
              />
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>User ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReferredUsers.length > 0 ? (
                    filteredReferredUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {user.id}
                        </TableCell>
                        <TableCell>{user.name || 'N/A'}</TableCell>
                        <TableCell>{user.email || 'N/A'}</TableCell>
                        <TableCell>{user.phone || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip 
                            label="Referred" 
                            color="success"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography variant="body1" color="textSecondary">
                          {selectedUser?.referredUsers.length === 0 
                            ? 'No referred users found.' 
                            : 'No referred users match your search criteria.'
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDetails} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserReferrals;

// Add CSS for better styling
const css = `
  .container {
    padding: 20px;
  }
  
  .MuiTableRow-hover:hover {
    background-color: #f5f5f5;
  }
  
  .MuiTableCell-root {
    padding: 16px;
  }
  
  .MuiChip-root {
    margin: 2px;
  }
  
  .MuiDialogContent-root {
    padding: 0;
  }
`;

document.head.insertAdjacentHTML('beforeend', `<style>${css}</style>`);